// @owners { team: patients }
import { Button, Icon, Illustration, Toast, ToastContext } from '@alto/design-system';
import { CommsVerificationCard } from '@alto/features';
import * as React from 'react';
import { View } from 'react-native';
import alertIssue from '~shared/assets/images/alert_issue.png';
import { getCompanyName } from '~shared/config/getCompanyName';
import { COMMS_CHANNEL, IMG_ALT_TEXTS } from '~shared/constants';
import { useRequestVerification } from '~shared/queries/query-keys/verifications';
import { useNavigateAfterVerification } from '../hooks/useNavigateAfterVerification';

type Props = {
  readonly authorized: boolean;
  readonly commsChannel: keyof typeof COMMS_CHANNEL;
  readonly token: string;
};

export const CommsVerificationFail = ({ authorized, commsChannel, token }: Props) => {
  const { addToast } = React.useContext(ToastContext);
  const { mutateAsync: requestVerification, isPending: requestVerificationLoading } = useRequestVerification();

  const [isVerificationSent, setIsVerificationSent] = React.useState(false);
  const formattedChannelText = COMMS_CHANNEL[commsChannel].toLowerCase();

  const navigateToNextRoute = useNavigateAfterVerification(authorized);

  const handleSendingVerification = async () => {
    try {
      await requestVerification({ token, commsChannel });
      addToast(<Toast variant="success">Verification link has been sent to your {formattedChannelText}</Toast>);
      setIsVerificationSent(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'something went wrong';
      addToast(<Toast variant="error">Oops, {message}</Toast>);
      setIsVerificationSent(false);
    }
  };

  return (
    <View>
      <CommsVerificationCard
        headerIcon={
          <Illustration
            size="lg"
            source={alertIssue}
            accessibilityLabel={IMG_ALT_TEXTS.alertIssue}
          />
        }
        title="Verification failed"
        text={`Your link has expired or an error occurred while attempting to verify your ${formattedChannelText}.`}
        centerText
      >
        {isVerificationSent ? (
          <Icon
            name="checkcircle-small"
            type="secondary"
          />
        ) : (
          <Button
            width="full"
            disabled={requestVerificationLoading}
            loading={requestVerificationLoading}
            label="Send new verification link"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onPress={handleSendingVerification}
          />
        )}
        <Button
          type="secondary"
          width="full"
          label={`Continue to ${getCompanyName()}`}
          accessibilityLabel={`Continue to ${getCompanyName()}`}
          onPress={navigateToNextRoute}
        />
      </CommsVerificationCard>
    </View>
  );
};
